import { useEffect, useState } from "react";
import { Copy, Gift, Loader2, Mail, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/auth";

const input = "w-full bg-[#0F070B] border border-[#D9B47A]/25 px-4 py-3 text-[16px] text-[#F2E8DF] placeholder:text-[#8F8176] focus:outline-none focus:border-[#E8838E]";

// Ariel emails the partner a warm invitation with the personal link and partner price.
const SendInvite = ({ lastSent, onSent }) => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/partner-invite/send", form);
      toast.success(`Ariel’s invitation is on its way to ${form.name}.`);
      setForm({ name: "", email: "" });
      onSent(data?.sent ?? null);
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={send} data-testid="partner-invite-form" className="mt-5 border-t border-[#E8838E]/20 pt-5">
      <p className="text-[13px] text-[#A8998C]">Or let Ariel invite them by email</p>
      <div className="mt-3 grid gap-2">
        <input data-testid="partner-invite-name" required maxLength={80} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Your partner’s name" className={input} />
        <input data-testid="partner-invite-email" required type="email" maxLength={200} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Their email" className={input} />
      </div>
      <button data-testid="partner-invite-send" disabled={busy} className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#E8838E] text-[#0A0706] py-3 font-tt-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send Ariel’s invitation</button>
      {lastSent && <p data-testid="partner-invite-last" className="mt-3 text-[13px] text-[#A8998C]">Last sent to {lastSent.name} ({lastSent.email}) on {new Date(lastSent.at).toLocaleDateString()}</p>}
    </form>
  );
};

// Settings: the initiate's personal partner invitation (25% off either app or the bundle, one partner).
export default function PartnerInviteCard() {
  const [inv, setInv] = useState(null);
  useEffect(() => {
    api.get("/partner-invite").then(({ data }) => setInv(data ?? null)).catch(() => {});
  }, []);
  useEffect(() => {
    if (inv && window.location.hash === "#partner") document.getElementById("partner")?.scrollIntoView({ behavior: "smooth" });
  }, [inv]);
  if (!inv?.eligible) return null;
  const link = `${window.location.origin}${inv.path}`;
  const share = async () => {
    if (navigator.share) await navigator.share({ title: "The Tantric Temple", text: "Walk the gates with me. Your partner invitation saves you 25%.", url: link }).catch(() => {});
    else {
      await navigator.clipboard?.writeText(link).catch(() => {});
      toast.success("Partner invitation copied.");
    }
  };
  return (
    <section id="partner" data-testid="partner-invite-card" className="mt-6 scroll-mt-6 border border-[#E8838E]/30 bg-[#140910] p-6">
      <p className="font-display text-xl flex items-center gap-2"><Gift className="w-4 h-4 text-[#E8838E]" /> Invite your partner</p>
      <p className="mt-2 text-sm text-[#CFC2B6] leading-relaxed">Your partner saves 25% on either app or both: $223 instead of $297, or $373 for the bundle. One partner per initiate.</p>
      {inv.partner ? (
        <p data-testid="partner-invite-claimed" className="mt-4 text-[15px] text-[#F2E8DF]">Claimed by {inv.partner.name}{inv.partner.joined ? " · walking the path with you" : " · not yet unlocked"}</p>
      ) : (
        <>
          <p data-testid="partner-invite-link" className="mt-4 break-all font-tt-mono text-[13px] text-[#D9B47A]">{link}</p>
          <div className="mt-4 flex gap-2">
            <button data-testid="partner-invite-share" onClick={share} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-[#E8838E]/50 py-3 font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#E8838E] hover:bg-[#E8838E] hover:text-[#0A0706] transition-colors"><Share2 className="w-4 h-4" /> Share invitation</button>
            <button data-testid="partner-invite-copy" aria-label="Copy link" onClick={() => navigator.clipboard?.writeText(link).then(() => toast.success("Link copied.")).catch(() => {})} className="w-12 inline-flex items-center justify-center rounded-full border border-[#E8838E]/50 text-[#E8838E] hover:bg-[#E8838E] hover:text-[#0A0706] transition-colors"><Copy className="w-4 h-4" /></button>
          </div>
          <SendInvite lastSent={inv.last_sent} onSent={(sent) => setInv((x) => ({ ...x, last_sent: sent }))} />
        </>
      )}
    </section>
  );
}
